// src/modules/app-views/serviceOrders/pages/Init/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useCallback, useRef } from 'react';
import { Table, Tooltip, Button, Space, Dropdown } from 'antd';
import {
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
  ContainerOutlined,
  RestOutlined,
} from '@ant-design/icons';
import { PiLifebuoy } from 'react-icons/pi';
import { useListContext } from 'components/core/ListProvider';
import { useModuleContext } from 'components/core/ModuleProvider';
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import { isMobileView } from 'utils/helpers';
import {
  numberColumn,
  customerColumn,
  statusColumn,
  totalColumn,
} from './components/tableColumns';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ServiceOrdersInit = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    data,
    loading,
    tableKey,
    pagination,
    handleTableChanges,
    rowSelection,
    buildTableColumns,
    availableTableColumns,
    buildNumberLineColumn,
    buildDocumentCodeColumn,
    buildCreatedAtColumn,
    buildUpdatedAtColumn,
    buildDocumentInfoIconsColumn,
    buildActionsButtonColumn,
    buildColumnProps,
    query,
    tableComponents,
    hiddenColumns,
    isEditingTable,
    TableFeatures,
    refresh,
  } = useListContext();

  const {
    handleEditDocument,
    handleArchiveDocuments,
    handleRemoveDocuments,
    handleRestoreDocuments,
    handlePreviewDocument,
  } = useModuleContext();

  const storedListSettings = useZustand('module', 'listSettings');
  const storedLanguage = useZustand('theme', 'language');

  // Current folder (supports 'inbox', 'documents', 'archive', 'recycle')
  const folder = storedListSettings?.filterDrawer?.folder || query?.drawer?.folder || 'inbox';

  // Serialize sort and search for stable dependencies
  const sortKey = JSON.stringify(query?.sort || []);
  const searchKey = JSON.stringify(query?.search || {});

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const isMobile = appService.isMobile();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. REFS & HANDLERS (using refs to maintain stable function identity)
  // ─────────────────────────────────────────────────────────────────────────────

  // Store handlers in refs to avoid re-creating render functions
  const handlersRef = useRef({});
  handlersRef.current = {
    handleEditDocument,
    handleArchiveDocuments,
    handleRemoveDocuments,
    handleRestoreDocuments,
    handlePreviewDocument,
    refresh,
    folder,
  };

  // Stable render function for DOCUMENTS folder
  const renderDocumentsActions = useCallback((_, record) => {
    const {
      handleEditDocument,
      handleArchiveDocuments,
      handleRemoveDocuments,
      handlePreviewDocument,
      refresh,
    } = handlersRef.current;

    const actionOptions = { onSuccess: refresh };

    const allButtons = [
      {
        key: 'view',
        icon: <EyeOutlined />,
        label: translateX('view'),
        onClick: () => handlePreviewDocument?.(record),
      },
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: translateX('edit'),
        onClick: () => handleEditDocument?.(record),
      },
      {
        key: 'archive',
        icon: <ContainerOutlined />,
        label: translateX('archive'),
        onClick: () => handleArchiveDocuments?.([record], actionOptions),
      },
      {
        key: 'remove',
        icon: <RestOutlined />,
        label: translateX('remove'),
        onClick: () => handleRemoveDocuments?.([record], actionOptions),
        danger: true,
      },
    ];

    return renderActionButtons(allButtons);
  }, []);

  // Stable render function for ARCHIVE and RECYCLE folders
  const renderArchiveRecycleActions = useCallback((_, record) => {
    const { handleRestoreDocuments, handleRemoveDocuments, refresh, folder } = handlersRef.current;
    const isRecycle = folder === 'recycle';
    const actionOptions = { onSuccess: refresh };

    const allButtons = [
      {
        key: 'restore',
        icon: <PiLifebuoy />,
        label: translateX('restore'),
        onClick: () => handleRestoreDocuments?.([record], actionOptions),
      },
      {
        key: 'remove',
        icon: <RestOutlined />,
        label: isRecycle ? translateX('permanently_remove') : translateX('remove'),
        onClick: () => handleRemoveDocuments?.([record], actionOptions),
        danger: isRecycle,
      },
    ];

    return renderActionButtons(allButtons);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // Build table columns
  useEffect(() => {
    buildTableColumns?.([
      buildNumberLineColumn(),

      buildDocumentCodeColumn(),

      numberColumn(buildColumnProps),

      customerColumn(buildColumnProps),

      statusColumn(buildColumnProps),

      totalColumn(buildColumnProps),

      buildCreatedAtColumn(),

      buildUpdatedAtColumn(),

      buildDocumentInfoIconsColumn(),

      buildActionsButtonColumn({
        view: false,
        customRender: ['archive', 'recycle'].includes(folder)
          ? renderArchiveRecycleActions
          : renderDocumentsActions,
      }),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storedListSettings?.showNumberOfLine,
    sortKey,
    searchKey,
    storedLanguage,
    hiddenColumns,
    isEditingTable,
    query?.drawer?.folder,
    folder,
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <TableFeatures />

      <Table
        key={tableKey}
        scroll={{ x: 'max-content' }}
        bordered
        rowKey="_id"
        size={isMobile ? 'small' : 'large'}
        columns={availableTableColumns}
        dataSource={data || []}
        pagination={pagination}
        className="with-checkbox"
        loading={loading}
        onChange={handleTableChanges}
        rowSelection={rowSelection}
        components={tableComponents}
      />
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ServiceOrdersInit;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Renders action buttons with responsive behavior
 * - Desktop: up to 4 visible buttons (if more, show 3 + MORE)
 * - Mobile: 1 visible if only 1 button, otherwise all in MORE
 * @param {Array} buttons - Array of button configs
 * @returns {JSX.Element}
 */
function renderActionButtons(buttons) {
  if (!buttons || buttons.length === 0) return null;

  const isMobile = isMobileView();

  if (isMobile) {
    if (buttons.length === 1) {
      const btn = buttons[0];
      return (
        <Tooltip title={btn.label}>
          <Button
            size="small"
            type="text"
            icon={btn.icon}
            onClick={btn.onClick}
            disabled={btn.disabled}
            danger={btn.danger}
          />
        </Tooltip>
      );
    }

    const allDisabled = buttons.every(btn => btn.disabled);
    const menuItems = buttons.map(btn => ({
      key: btn.key,
      icon: btn.icon,
      label: btn.label,
      danger: btn.danger,
      disabled: btn.disabled,
      onClick: btn.onClick,
    }));

    return (
      <Dropdown menu={{ items: menuItems }} trigger={['click']} disabled={allDisabled}>
        <Tooltip title={translateX('more')}>
          <Button size="small" type="text" icon={<MoreOutlined />} disabled={allDisabled} />
        </Tooltip>
      </Dropdown>
    );
  }

  if (buttons.length <= 4) {
    return (
      <Space size={4}>
        {buttons.map(btn => (
          <Tooltip key={btn.key} title={btn.label}>
            <Button
              size="small"
              type="text"
              icon={btn.icon}
              onClick={btn.onClick}
              disabled={btn.disabled}
              danger={btn.danger}
            />
          </Tooltip>
        ))}
      </Space>
    );
  }

  const visibleButtons = buttons.slice(0, 3);
  const menuButtons = buttons.slice(3);
  const allMenuDisabled = menuButtons.every(btn => btn.disabled);

  const menuItems = menuButtons.map(btn => ({
    key: btn.key,
    icon: btn.icon,
    label: btn.label,
    danger: btn.danger,
    disabled: btn.disabled,
    onClick: btn.onClick,
  }));

  return (
    <Space size={4}>
      {visibleButtons.map(btn => (
        <Tooltip key={btn.key} title={btn.label}>
          <Button
            size="small"
            type="text"
            icon={btn.icon}
            onClick={btn.onClick}
            disabled={btn.disabled}
            danger={btn.danger}
          />
        </Tooltip>
      ))}
      <Dropdown menu={{ items: menuItems }} trigger={['click']} disabled={allMenuDisabled}>
        <Tooltip title={translateX('more')}>
          <Button size="small" type="text" icon={<MoreOutlined />} disabled={allMenuDisabled} />
        </Tooltip>
      </Dropdown>
    </Space>
  );
}
